import React, { useContext, useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ObjectShow from "@components/objects/ObjectShow";
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import ObjectColumn from "@components/objects/ObjectColumn";
import { ObjectPrimaryInfo } from "@components/objects/ObjectPrimaryInfo";
import Avatar from "@mui/joy/Avatar";
import { ObjectContext } from "@providers/ObjectProvider";
import { ensureDateObject, formatAbsoluteDate, formatDate, isValidDate } from "@utils/dates";
import ObjectPreview from "@components/objects/ObjectPreview";
import { generateAddress } from "@utils/generateAddress";
import { CardOverflow, List, ListDivider, ListItem, ListItemContent, ListItemDecorator, Table } from "@mui/joy";
import AbstractObjectRelationship from "@components/objects/AbstractObjectRelationship";
import UserAccessCard from "@components/objects/UserAccessCard";
import { intervalToDuration } from "date-fns";
import ObjectEdit from "@components/objects/ObjectEdit";
import { ObjectPrimaryInfoEdit } from "@components/objects/ObjectPrimaryInfoEdit";
import { ObjectRelationshipEdit } from "@components/objects/ObjectRelationshipEdit";
import { countries } from "@utils/selectOptions";
import { ObjectRelationship } from "@components/objects/ObjectRelationship";
import SearchSelect from "@components/SearchSelect";
import { CheckPermission } from "@components/CheckPermission";
import { useDeleteBusiness, useGetBusiness, useUpdateBusiness } from "@api/businessService";
import { ContextualHelp } from "@components/ContextualHelp";
import { useDeleteProperty, useGetProperty, useUpdateProperty } from "@api/propertyService";
import { ownerEndpoints } from "@utils/morphTypes";

// import { DynamicFieldData } from "./dynamic-control-types";

const fields /*: DynamicFieldData[]*/ = [
    {
        name: 'owner_id',
        label: 'Owner',
        type: 'relationship',
        polymorphic_name: 'owner_type',
        endpoint: ownerEndpoints.join(','),
        defaultProp: 'owner',
    },

    {
        name: 'address',
        type: 'text',
        placeholder: 'e.g. 123 Main Street',
        showRequired: true,
        config: {
            required: true,
        }
    },
    {name: 'address_2', label: <>Address <br/>(cont.)</>, type: 'text', placeholder: 'e.g. Suite A'},
    {name: 'country', type: 'select', options: countries, defaultValue: ({tenant}) => tenant.country},
    {
        name: 'zip_postal',
        label:<>Zip/<wbr/>Postal</>,
        type: 'text',
        placeholder: 'e.g. 61325',
        defaultValue: ({tenant}) => tenant.postal,
        /*
        onChange: (value, formState_wip) => {
            prefillCityProvince
        }
        */
    },
    {name: 'city', type: 'text', placeholder: 'e.g. MainStreet', defaultValue: ({tenant}) => tenant.city,},
    {name: 'state_province', label: 'State', type: 'text', placeholder: 'e.g. IL', defaultValue: ({tenant}) => tenant.state,},

    {
        name: 'latitude',
        label: <ContextualHelp slug="latitude">Latitude</ContextualHelp>,
        type: 'text',
        placeholder: '#',
    },
    {
        name: 'longitude',
        label: <ContextualHelp slug="longitude">Longitude</ContextualHelp>,
        type: 'text',
        placeholder: '#',
    },

    {name: 'parcel_number', type: 'text', placeholder: 'e.g. 422206030140'},
    {name: 'gis_url', label: 'GIS Link', type: 'text', placeholder: 'e.g. https://mainstgis.net/parcel/422206030140'},

    {
        name: 'land_use_id',
        label: 'Land Use',
        type: 'relationship',
        endpoint: 'land-use',
        defaultProp: 'land_use',
        loadAll: true,
    },

    {name: 'square_footage', type: 'number', placeholder: '#'},
    {name: 'floors', label: '# of Floors', type: 'number', placeholder: '#'},
    {name: 'residential_units', type: 'number', placeholder: '#'},
    {name: 'residential_only', type: 'boolean'},
    {name: 'commercial_units', type: 'number', placeholder: '#'},

    {
        name: 'tif_id',
        label: 'TIF',
        type: 'relationship',
        endpoint: 'tifs',
        defaultProp: 'tif',
        loadAll: true,
    },
    {
        name: 'ssa_id',
        label: 'SSA/BID',
        type: 'relationship',
        endpoint: 'ssas',
        defaultProp: 'ssa',
        loadAll: true,
    },
    {
        name: 'zone_id',
        label: 'Zoning',
        type: 'relationship',
        endpoint: 'zones',
        defaultProp: 'zone',
        loadAll: true,
    },

    {name: 'enterprise', label:'Enterprise Zone', type: 'boolean'},
    {name: 'for_sale', type: 'boolean'},
    {name: 'for_lease', type: 'boolean'},
    {
        name: 'asking_price',
        type: 'number',
        placeholder: '123',
        startDecorator: '$',
    },
    {
        name: 'assessed_market_value',
        label: <>Assessed<br/>Market Value</>,
        type: 'number',
        placeholder: '123',
        startDecorator: '$',
    },
    {
        name: 'private_parking',
        type: 'number',
        placeholder: '#',
    },

    {name: 'architect', type: 'text', placeholder: 'e.g. Neo, Smith & Associates'},
    {name: 'built_date', label: 'Built', type: 'date', placeholder: 'e.g. 05/23/2016'},
    {name: 'lost_date', label: 'Lost Date', type: 'date', placeholder: 'e.g. 05/23/2016'},
    {name: 'lost_reason', type: 'text', placeholder: 'e.g. Fire'},

    // notes
    {
        name: 'demographics',
        label: 'Demographics',
        type: 'demographics',
        suffix: '-owned',
    },

];

const CardHeader = () => {
    const data = useContext(ObjectContext);
    const avatarInitials = data.name ? data.name.split(' ').map(i => i.charAt(0)).join('').toUpperCase() : '';

    return (
    <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={2}
        p={1.5}
        pt={0}
    >
        <Avatar size="lg" sx={{'--Avatar-size': '5rem'}} src={data?.profile_image?.url}>{avatarInitials}</Avatar>
        <Box sx={{flex: 1}}>
            <Typography fontSize={'md'} fontWeight={'bold'}>{data.name}</Typography>
            {data.title && <Typography fontSize={'sm'}>{data.title}</Typography>}
            {data.company_name && data.name !== data.company_name && <Typography fontSize={'sm'}>{data.company_name}</Typography>}
        </Box>
    </Stack>
)
};

export default function PropertyEdit(props) {
    return (
        <ObjectEdit
            query={useGetProperty}
            mutation={useUpdateProperty}
            deleteMutation={useDeleteProperty}
            type={'property'}
            permissionType={'properties'}
            title={'Edit Property'}
            subtitleKey={'name'}
            color={'property'}
            {...props}
        >
            <ObjectColumn primary>
                <ObjectPrimaryInfoEdit
                    title={'Property Info'}
                    tint={'property'}
                    fields={fields}
                    // cardHeader={<CardHeader/>}
                />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectRelationshipEdit type={'contacts'} permissions={'edit properties'} />
                <ObjectRelationshipEdit type={'involvements'} permissions={'edit properties'} />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectRelationshipEdit type={'notes'} permissions={'edit properties'} />
                <ObjectRelationshipEdit type={'registries'} permissions={'edit properties'} />
                <ObjectRelationshipEdit type={'sales'} permissions={'edit properties'} />
                <ObjectRelationshipEdit type={'files'} permissions={'edit properties'} />
            </ObjectColumn>

            <ObjectColumn forceBreak/>

            <ObjectColumn>
                <ObjectRelationshipEdit type={'property_businesses'} column="businesses" permissions={'edit properties'} />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectRelationshipEdit type={'improvements'} permissions={'edit properties'} />
            </ObjectColumn>

            <ObjectColumn full>
                <ObjectRelationshipEdit type={'property_units'} permissions={'edit properties'} />
            </ObjectColumn>

        </ObjectEdit>
    )
}
